<script setup lang="ts">
import CoreIcon from '@core/core-icon.vue';
import {computed, PropType} from 'vue';

type IconObject = {
  name: string,
  viewBox?: string,
}

const props = defineProps({
	icon: {
		type: Object as PropType<IconObject>,
		default: null
	},
	to: {
		type: String,
		default: ''
	},
	src: {
		type: String,
		default: ''
	},
	white: {
		type: Boolean,
		default: false
	},
	gray: {
		type: Boolean,
		default: false
	},
	gray_dark: {
		type: Boolean,
		default: false
	},
	light: {
		type: Boolean,
		default: false
	},
	pink: {
		type: Boolean,
		default: false
	},
	small: {
		type: Boolean,
		default: false
	},
  ultra_small: {
		type: Boolean,
		default: false
	},
  shadow: {
    type: Boolean,
    default: false
  }
});
const modes = computed(() => ({
	'part-icon-circle_gray-dark': props.gray_dark,
	'part-icon-circle_light': props.light,
	'part-icon-circle_pink': props.pink,
	'part-icon-circle_white': props.white,
	'part-icon-circle_small': props.small,
	'part-icon-circle_ultra-small': props.ultra_small,
	'part-icon-circle_shadow': props.shadow
}));
</script>

<template>
	<component
		:is="to ? 'router-link' : 'div'"
		:to="to || null"
		:class="['part-icon-circle', modes]"
	>
		<core-icon
			v-if="icon"
			class="part-icon-circle__icon"
			:name="icon.name"
			:viewBox="icon?.viewBox"
		>
		</core-icon>
		<img
			v-else-if="src"
			:src="src"
			alt=""
			class="part-icon-circle__icon"
		>
	</component>
</template>

<style lang="scss">
.part-icon-circle {
	--button-size: 5rem;
	--button-small-size: 3.5rem;
	--button-ultra-small-size: 3rem;
	--button-icon-size: 1.8rem;
	--button-small-icon-size: 2rem;
	--button-ultra-small-icon-size: 1.5rem;

	width: var(--button-size);
	height: var(--button-size);
	flex-shrink: 0;
	cursor: pointer;

	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.17);
	fill: #fff;
	transition: ease .2s;

	&:active {
		background: rgba(255, 255, 255, 0.3);
	}

	// Подсветка там, где есть курсор, а не только на широком экране: иконка
	// чуть растёт, подложка светлеет — это кнопка, и она должна отзываться
	@media (hover: hover) and (pointer: fine) {
		&:hover {
			background: rgba(255, 255, 255, 0.28);

			.part-icon-circle__icon {
				transform: scale(1.1);
			}
		}
	}

	&_small {
		width: var(--button-small-size);
		height: var(--button-small-size);

		.part-icon-circle__icon {
		width: var(--button-small-icon-size);
		}
	}

	&_ultra-small {
		width: var(--button-ultra-small-size);
		height: var(--button-ultra-small-size);

		.part-icon-circle__icon {
		width: var(--button-ultra-small-icon-size);
		}
	}

	&_white {
		fill: var(--color-active);
		background: var(--color-white);
		@include theme-dark {
			background: var(--color-panel);
			box-shadow: inset 1px 1px 1px 0px var(--color-panel);
		}

		@media (hover: hover) and (pointer: fine) {
			&:hover {
				background: rgba(var(--color-active-rgb), 0.12);
			}
		}
	}

	&_gray-dark {
		background: #292D32;
		fill: #ffffff;

		&:active {
			background: #393e45;
		}
	}

	&_light {
		background: var(--color-panel);
		fill: var(--color-active);
		transition: background-color ease 0.2s;

		@media (hover: hover) and (pointer: fine) {
			&:hover {
				background: rgba(var(--color-active-rgb), 0.1);
			}
		}

		&:active {
			background: rgba(239, 230, 246, 1);
		}
	}

	&_pink {
		background: rgba(239, 230, 246);
		fill: #1B213F;

		&:active {
		background: rgb(234, 220, 246);
		}
	}
	&_shadow {
		box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
	}

	&__icon {
		transition: transform 0.2s;
		width: var(--button-icon-size);
	}
}
</style>

